// API client shared by every feature. JSON in and out; writes carry X-PR-CSRF from the pr_csrf cookie.

export class ApiError extends Error {
  status: number
  code: string
  field?: string      // the input that failed, when the API names one

  constructor(status: number, code: string, message: string, field?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.field = field
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** The error for a failed response. Our API sends {error, message}; anything else (a proxy's 502 page, a 413 from
 * the load balancer) still gets one plain line, never "Internal Server Error" or a blank. */
export function errorFrom(status: number, data: unknown): ApiError {
  const err = (data ?? {}) as { error?: string; message?: string; field?: string }
  const plain = status === 413 ? 'The file is too large.'
    : status >= 500 ? 'Something went wrong on our side. Try again.' : "That didn't work. Try again."
  return new ApiError(status, err.error ?? `http_${status}`, err.message || plain, typeof err.field === 'string' ? err.field : undefined)
}

export function networkError(): ApiError {
  return new ApiError(0, 'network', "Couldn't reach ernd. Check your connection and try again.")
}

export function readCookie(name: string): string | undefined {
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const c = part.trim()
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length))
  }
  return undefined
}

/** A File or Blob body goes as raw bytes (a logo upload); anything else as JSON. `keepalive`: the request outlives the
 * page, for a last save as it goes away. */
export async function api<T>(method: Method, path: string, body?: unknown, opts?: { keepalive?: boolean }): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const raw = body instanceof Blob
  if (body !== undefined) headers['Content-Type'] = raw ? (body.type || 'application/octet-stream') : 'application/json'
  if (method !== 'GET') {
    const csrf = readCookie('pr_csrf')
    if (csrf) headers['X-PR-CSRF'] = csrf
  }
  let res: Response
  let text: string
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      keepalive: opts?.keepalive,
      body: body === undefined ? undefined : raw ? body : JSON.stringify(body),
    })
    text = await res.text()
  } catch {
    throw networkError()
  }
  let data: unknown = undefined
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = undefined
    }
  }
  if (!res.ok) throw errorFrom(res.status, data)
  return data as T
}
